import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';
import { InternalTrailerType, TrailerCostPayee, TrailerDocumentType } from 'src/types/enums';

import type {
  TrailerCheckListItemResponse,
  TrailerCheckDetailResponse,
  TrailerCheckStatusType,
  TrailerCheckNoteResponse,
  CreateInternalTrailerCheckBody,
  ResolveTrailerCheckBody,
  AddTrailerCheckNoteBody,
  AddTrailerCheckPhotosBody,
  UpdateTrailerCheckResolutionBody,
  UpdateTrailerCheckNoteBody,
  UpdateTrailerCheckBody,
  TrailerCheckEventTypeType,
} from './endpoints/TrailerCheckEndpoint.types';
import type { TrailerByIdResponse, TrailerDetail } from './endpoints/TrailerEndpoint.types';
import type { CreateTrailerResponse } from './endpoints/CreateTrailerEndpoint.types';
import type { TrailerOverviewResponse } from './endpoints/TrailerOverviewEndpoint.types';
import type { UpdateTrailerResponse } from './endpoints/UpdateTrailerEndpoint.types';
import type { LinkTrailerDocumentResponse } from './endpoints/LinkTrailerDocumentEndpoint.types';
import type { UnlinkTrailerDocumentResponse } from './endpoints/UnlinkTrailerDocumentEndpoint.types';
import type {
  TrailerCostByIdResponse,
  TrailerCostsResponse,
  TrailerCostMutationResponse,
} from './endpoints/TrailerCostEndpoint.types';
import type {
  TelemetryTrailerListItem,
  TelemetryTrailerListResponse,
} from '../telemetry/endpoints/TrailerTrackingEndpoints.types';

export type {
  TrailerCheckListItemResponse,
  TrailerCheckDetailResponse,
  TrailerCheckStatusType,
  TrailerCheckNoteResponse,
  TelemetryTrailerListItem,
  TelemetryTrailerListResponse,
};

// Cleaner re-exports for consumers.
export type Trailer = TrailerDetail;

export const TRAILER_SWR_KEY = 'trailer';
export const TRAILER_COST_SWR_KEY = 'trailer-cost';
export const TRAILER_OVERVIEW_SWR_KEY = 'trailer-overview';
export type { TrailerOverviewResponse };

export type TrailerCheckSortBy = 'eventDate' | 'createdAt' | 'status' | 'cannotContinue' | 'resolved' | 'eventType';

export type { TrailerCheckEventTypeType };

export async function fetchTrailerChecks(opts?: {
  trailerId?: number;
  status?: TrailerCheckStatusType;
  resolved?: boolean;
  cannotContinue?: boolean;
  driverId?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: TrailerCheckSortBy;
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}): Promise<TrailerCheckListItemResponse[]> {
  const params = new URLSearchParams();
  if (opts?.trailerId) params.set('trailerId', opts.trailerId.toString());
  if (opts?.status) params.set('status', opts.status);
  if (opts?.resolved !== undefined) params.set('resolved', opts.resolved.toString());
  if (opts?.cannotContinue !== undefined) params.set('cannotContinue', opts.cannotContinue.toString());
  if (opts?.driverId) params.set('driverId', opts.driverId);
  if (opts?.dateFrom) params.set('dateFrom', opts.dateFrom);
  if (opts?.dateTo) params.set('dateTo', opts.dateTo);
  if (opts?.sortBy) params.set('sortBy', opts.sortBy);
  if (opts?.sortOrder) params.set('sortOrder', opts.sortOrder);
  if (opts?.limit) params.set('limit', opts.limit.toString());
  if (opts?.offset) params.set('offset', opts.offset.toString());

  const queryString = params.toString();
  const url = queryString
    ? `/api/v1/internal/trailer/trailer-check?${queryString}`
    : '/api/v1/internal/trailer/trailer-check';
  return fetchEndpointData<TrailerCheckListItemResponse[]>(url);
}

export async function fetchTrailerCheck(checkId: string): Promise<TrailerCheckDetailResponse> {
  return fetchEndpointData<TrailerCheckDetailResponse>(`/api/v1/internal/trailer/trailer-check/${checkId}`);
}

export async function createInternalTrailerCheck(
  body: CreateInternalTrailerCheckBody,
): Promise<TrailerCheckDetailResponse> {
  return fetchEndpointData<TrailerCheckDetailResponse>('/api/v1/internal/trailer/trailer-check', {
    method: 'POST',
    body,
  });
}

export interface TrailerListItem {
  id: number;
  name: string;
  licensePlate: string;
  isActive: boolean;
}

export async function fetchTrailerList(opts?: { search?: string; isActive?: boolean }): Promise<TrailerListItem[]> {
  const params = new URLSearchParams();
  if (opts?.search) params.set('search', opts.search);
  if (opts?.isActive !== undefined) params.set('isActive', opts.isActive.toString());

  const queryString = params.toString();
  const url = queryString ? `/api/v1/internal/trailer/trailers?${queryString}` : '/api/v1/internal/trailer/trailers';
  return fetchEndpointData<TrailerListItem[]>(url);
}

export async function resolveTrailerCheck(
  checkId: string,
  body: ResolveTrailerCheckBody,
): Promise<TrailerCheckDetailResponse> {
  return fetchEndpointData<TrailerCheckDetailResponse>(`/api/v1/internal/trailer/trailer-check/${checkId}/resolve`, {
    method: 'PATCH',
    body,
  });
}

export async function addTrailerCheckNote(
  checkId: string,
  body: AddTrailerCheckNoteBody,
): Promise<TrailerCheckNoteResponse> {
  return fetchEndpointData<TrailerCheckNoteResponse>(`/api/v1/internal/trailer/trailer-check/${checkId}/notes`, {
    method: 'POST',
    body,
  });
}

export async function addTrailerCheckPhotos(
  checkId: string,
  body: AddTrailerCheckPhotosBody,
): Promise<TrailerCheckDetailResponse> {
  return fetchEndpointData<TrailerCheckDetailResponse>(`/api/v1/internal/trailer/trailer-check/${checkId}/photos`, {
    method: 'POST',
    body,
  });
}

export async function updateTrailerCheckResolution(
  checkId: string,
  body: UpdateTrailerCheckResolutionBody,
): Promise<TrailerCheckDetailResponse> {
  return fetchEndpointData<TrailerCheckDetailResponse>(`/api/v1/internal/trailer/trailer-check/${checkId}/resolution`, {
    method: 'PATCH',
    body,
  });
}

export async function updateTrailerCheck(
  checkId: string,
  body: UpdateTrailerCheckBody,
): Promise<TrailerCheckDetailResponse> {
  return fetchEndpointData<TrailerCheckDetailResponse>(`/api/v1/internal/trailer/trailer-check/${checkId}`, {
    method: 'PATCH',
    body,
  });
}

export async function updateTrailerCheckNote(
  checkId: string,
  noteId: string,
  body: UpdateTrailerCheckNoteBody,
): Promise<TrailerCheckNoteResponse> {
  return fetchEndpointData<TrailerCheckNoteResponse>(
    `/api/v1/internal/trailer/trailer-check/${checkId}/notes/${noteId}`,
    {
      method: 'PATCH',
      body,
    },
  );
}

// ─── Trailer queries (migrated from GraphQL trailer/trailers) ─────────

export async function getTrailerOverview(): Promise<TrailerOverviewResponse> {
  return fetchEndpointData<TrailerOverviewResponse>('/api/v1/internal/trailer/trailer-overview');
}

export async function getTrailer(id: number): Promise<TrailerByIdResponse> {
  return fetchEndpointData<TrailerByIdResponse>(`/api/v1/internal/trailer/trailer/${id}`);
}

export function useTrailerById(id: number) {
  return useSWR([TRAILER_SWR_KEY, id], () => getTrailer(id));
}

// ─── Trailer cost queries ────────────────────────────────────────────

export async function getTrailerCosts(
  trailerId: number,
  cursor?: number | null,
  take?: number,
): Promise<TrailerCostsResponse> {
  const params = new URLSearchParams();
  params.set('trailerId', trailerId.toString());
  if (cursor != null) params.set('id', cursor.toString());
  if (take != null) params.set('take', take.toString());

  return fetchEndpointData<TrailerCostsResponse>(`/api/v1/internal/trailer/trailer-cost?${params.toString()}`);
}

export async function getTrailerCost(id: number): Promise<TrailerCostByIdResponse> {
  return fetchEndpointData<TrailerCostByIdResponse>(`/api/v1/internal/trailer/trailer-cost/${id}`);
}

export function useTrailerCost(id: number) {
  return useSWR([TRAILER_COST_SWR_KEY, id], () => getTrailerCost(id));
}

// ─── Trailer mutations (fetchers throw on error) ─────────────────────

export interface CreateTrailerInput {
  name: string;
  licensePlate: string;
  brand: string;
  serialNumber: string;
  responsibleId: string;
  registrationDate: string;
  insuranceExpirationDate?: string | null;
  inspectionExpirationDate?: string | null;
  trailerType?: InternalTrailerType;
  isRental?: boolean;
  rentalCompany?: string;
}

export async function createTrailer(data: CreateTrailerInput): Promise<CreateTrailerResponse> {
  return fetchEndpointData<CreateTrailerResponse>('/api/v1/internal/trailer/trailer', {
    method: 'POST',
    body: data,
  });
}

export interface UpdateTrailerInput {
  name?: string;
  licensePlate?: string;
  brand?: string;
  serialNumber?: string;
  responsibleId?: string;
  registrationDate?: string;
  insuranceExpirationDate?: string | null;
  inspectionExpirationDate?: string | null;
  isActive?: boolean;
  trailerType?: InternalTrailerType;
  isRental?: boolean;
  rentalCompany?: string;
}

export async function updateTrailer(id: number, data: UpdateTrailerInput): Promise<UpdateTrailerResponse> {
  return fetchEndpointData<UpdateTrailerResponse>(`/api/v1/internal/trailer/trailer/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function deleteTrailer(id: number): Promise<UpdateTrailerResponse> {
  return fetchEndpointData<UpdateTrailerResponse>(`/api/v1/internal/trailer/trailer/${id}`, {
    method: 'DELETE',
  });
}

export async function linkTrailerDocument(
  trailerId: number,
  documentId: string,
  documentType: TrailerDocumentType,
): Promise<LinkTrailerDocumentResponse> {
  return fetchEndpointData<LinkTrailerDocumentResponse>(`/api/v1/internal/trailer/trailer/${trailerId}/documents`, {
    method: 'POST',
    body: { documentId, documentType },
  });
}

export async function unlinkTrailerDocument(
  trailerId: number,
  documentId: string,
  documentType: TrailerDocumentType,
): Promise<UnlinkTrailerDocumentResponse> {
  return fetchEndpointData<UnlinkTrailerDocumentResponse>(`/api/v1/internal/trailer/trailer/${trailerId}/documents`, {
    method: 'DELETE',
    body: { documentId, documentType },
  });
}

// ─── Trailer cost mutations ──────────────────────────────────────────

export interface CreateTrailerCostInput {
  trailerId: number;
  amount: number;
  description: string;
  date: string;
  paidBy: TrailerCostPayee;
  customerId?: number | null;
  supplierId?: number | null;
  documentId?: string | null;
}

export async function createTrailerCost(data: CreateTrailerCostInput): Promise<TrailerCostMutationResponse> {
  return fetchEndpointData<TrailerCostMutationResponse>('/api/v1/internal/trailer/trailer-cost', {
    method: 'POST',
    body: data,
  });
}

export interface UpdateTrailerCostInput {
  trailerId?: number;
  amount?: number;
  description?: string;
  date?: string;
  paidBy?: TrailerCostPayee;
  customerId?: number | null;
  supplierId?: number | null;
  documentId?: string | null;
}

export async function updateTrailerCost(
  id: number,
  data: UpdateTrailerCostInput,
): Promise<TrailerCostMutationResponse> {
  return fetchEndpointData<TrailerCostMutationResponse>(`/api/v1/internal/trailer/trailer-cost/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function deleteTrailerCost(id: number): Promise<TrailerCostMutationResponse> {
  return fetchEndpointData<TrailerCostMutationResponse>(`/api/v1/internal/trailer/trailer-cost/${id}`, {
    method: 'DELETE',
  });
}

// ─── Trailer tracking (the fleet on the map) ─────────────────────────

/** Root of the trailer-tracking SWR key, which carries the filters with it. */
export const TRAILER_TRACKING_SWR_KEY = 'trailer-tracking';

export interface TrailerTrackingFilters {
  search?: string;
  /** "Has a tracker", not "has a position": a trailer whose unit went silent is still tracked. */
  onlyTracked?: boolean;
}

/**
 * The fleet as the map reads it: every trailer with the tracker following it and its last position.
 *
 * Deliberately unpaginated — a map cannot page, and the answer is bounded by the fleet itself. A trailer
 * with no tracker is still in the list, so the table is the complete answer and the map is a view of the
 * subset that can be drawn.
 */
export async function getTrailerTrackingList(
  filters: TrailerTrackingFilters = {},
): Promise<TelemetryTrailerListResponse> {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.onlyTracked) params.set('onlyTracked', 'true');

  const query = params.toString();
  return fetchEndpointData<TelemetryTrailerListResponse>(`/api/v1/telemetry/trailers${query ? `?${query}` : ''}`);
}

/**
 * `refreshInterval` is the live toggle, off by default: the endpoint fetches from the provider on every
 * call, so an interval is a real Suivo call each tick and the reader turns it on.
 */
export function useTrailerTrackingList(filters: TrailerTrackingFilters = {}, refreshInterval = 0) {
  return useSWR(
    [TRAILER_TRACKING_SWR_KEY, filters.search ?? '', filters.onlyTracked ?? false],
    () => getTrailerTrackingList(filters),
    { refreshInterval, keepPreviousData: true },
  );
}
